import { FormEvent, useRef, useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import { InputFieldOTP } from "../../components/ui/Inputs";
import { PrimaryButton } from "../../components/ui/Button";
import { useSelector, useDispatch } from "react-redux";
import Toast from "../../components/ui/Toast";
import { ROUTES } from "../../routes/routerConfig";
import {
  authSelector,
  setOtpUserData,
} from "../../services/redux/slices/authSlice";
import service from "../../services/api/service";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import { loginTypeData, maskEmail, maskMobile } from "../../utils/helpers";
import ResendOtpTimer from "../../components/common/ResendOtpTimer";
import { decodeJwtPayload, encrypt } from "../../utils/crypto";
import ButtonLoader from "../../components/ui/ButtonLoader";
import ReactGA from "react-ga4";
import { resetKeys } from "../../services/redux/slices/keyRegistrarSlice";
import { setTrackingId } from "../../services/redux/slices/stepperDynamicFormSlice";

const DocumentCollectionOtp = () => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");
  const source = params.get("source");
  const decrptedData = useMemo(() => decodeJwtPayload(token || ""), [token]);
  const numberOfDigits = 6;
  const inputRefs = useRef<HTMLInputElement[]>([]);
  const [otp, setOtp] = useState<string[]>(new Array(numberOfDigits)?.fill(""));
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "error" | "warning" | "info" | "success"
  >("success");
  const handleSnackbarClose = () => setSnackbarOpen(false);
  const handleSnackbarOpen = (message: string, severity: string) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity as "error" | "warning" | "info" | "success");
    setSnackbarOpen(true);
  };
  const isComplete = otp.every((val) => val !== "");
  const { otpUserData } = useSelector(authSelector);

  // show toast notification
  const handleToastOpen = (
    message: string,
    severity: "error" | "warning" | "info" | "success"
  ) => {
    setSnackbarOpen(true);
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
  };

  const handleClearedOtp = () => {
    setOtp(new Array(numberOfDigits).fill(""));
  };

  // Send OTP on page load based on decoded data
  useEffect(() => {
    if (decrptedData) {
      // storing application number in redux so we can use it on upload docs page
      dispatch(setTrackingId(decrptedData?.submissionTrackingId));

      // payload based on URL source (email/mobile)
      const loginId =
        source?.toLowerCase() === "email" && decrptedData?.email
          ? decrptedData.email
          : decrptedData?.mobile;

      if (!loginId) {
        handleToastOpen(
          "Required login data is missing in the token.",
          "error"
        );
        return;
      }

      const payload = {
        key: encrypt(loginId),
      };

      service?.auth
        ?.sendUserOtp(payload)
        .then((res) => {
          dispatch(resetKeys());
          handleToastOpen(res?.data?.message, "success");
          dispatch(setOtpUserData(res?.data?.data)); // store data in redux so we can use while resend OTP
          setIsLoading(false);
        })
        .catch((error) => {
          const errorMessage =
            error.response?.data?.message || "An error occurred";
          dispatch(
            setStatusPopup({
              status: "error",
              message: errorMessage,
            })
          );
          setIsLoading(false);
        });
    }
  }, [decrptedData, source]);

  // OTP input field onchange
  const handleOTPChange = (index: number, value: string) => {
    let newOTP = [...otp];
    const numericValue = value?.replace(/\D/g, "");
    const singleDigit = numericValue.slice(0, 1);
    if (singleDigit || value === "") {
      newOTP[index] = singleDigit;

      if (singleDigit && index < numberOfDigits - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
    setOtp(newOTP);
  };

  // managing OTP inputs onclick of reset
  const handleOtpReset = () => {
    setOtp(new Array(numberOfDigits)?.fill(""));
    inputRefs.current[0].focus();
  };

  // managing OTP inputs onclick of Backspace & Enter
  const handleBackspaceAndEnter = (e: any, index: number) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      inputRefs.current[index - 1].focus();
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (isComplete) {
        handleSubmit(e);
      }
    }
  };

  // user message for link verification flow
  const userOtpMessage = () => {
    const isEmail =
      otpUserData?.loginType?.toLowerCase() === "email" && otpUserData?.email;
    const loginTypeText = isEmail ? "email ID" : "mobile number";
    // const maskedValue = isEmail
    //   ? maskEmail(String(otpUserData?.email || "XXXXXXXXXX"))
    //   : maskMobile(String(otpUserData?.mobile || "XXXXXXXXXX"));

    return (
      <>
        Please enter the OTP sent to your registered {loginTypeText}{" "}
        {/* <span
          style={{
            color: "#1CB1B1",
            textDecoration: "none !important",
            WebkitTextDecorationLine: "none !important",
          }}
          className="text-disable"
        >
          {maskedValue}
        </span>{" "} */}
        to proceed with document collection.
      </>
    );
  };

  // api call for verify otp and navigation based on responce
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    ReactGA.event({
      category: "Document_Collection_OTP_Continue",
      action: "Document_Collection_OTP_Continue",
      label: "Document_Collection_OTP_Continue",
    });

    if (!token) {
      handleSnackbarOpen(
        "Verification failed: Missing security token.",
        "error"
      );
      return;
    }

    setIsLoading(true);
    const payload = {
      id: encrypt(loginTypeData(otpUserData)),
      otp: encrypt(otp.join("")),
    };

    service?.auth
      ?.verifyUserOtp(payload)
      .then((res: any) => {
        handleSnackbarOpen(
          "OTP verified successfully. Redirecting...",
          "success"
        );

        const documentUploadPath =
          ROUTES.DocumentCollection.uploadDocs.path.replace(
            ":applicationId",
            decrptedData?.submissionTrackingId
          );
        navigate(`/${documentUploadPath}`, {
          replace: true,
        });

        setIsLoading(false);
      })
      .catch((error: any) => {
        setIsLoading(false);
        const errorMessage =
          error.response?.data?.message ||
          "An error occurred during verification";

        dispatch(
          setStatusPopup({
            status: "error",
            message: errorMessage,
            isFunction: true,
            locationFunction: handleClearedOtp,
          })
        );
      });
  };

  return (
    <Box>
      <Box className="main-wrapper">
        <Box className="head-section">
          <Box className="heading" sx={{ lineHeight: " 0.41px" }}>
            Enter OTP
          </Box>
          <Box className="sub-heading">{userOtpMessage()}</Box>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <Box className="sub-heading">Type your 6 digit security code</Box>
          <Grid
            container
            alignItems="center"
            justifyContent="center"
            spacing={2}
          >
            {otp?.map((value, index) => (
              <Grid item key={index} xs={2}>
                <InputFieldOTP
                  variant="outlined"
                  type="tel"
                  inputMode="decimal"
                  autoComplete="one-time-code"
                  width={isXsOrSm ? "38px" : "100%"}
                  autoFocus={index === 0}
                  value={value}
                  onChange={(
                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                  ) => handleOTPChange(index, e.target.value)}
                  onKeyUp={(e) => handleBackspaceAndEnter(e, index)}
                  inputProps={{ maxLength: 1 }}
                  inputRef={(el) =>
                    (inputRefs.current[index] = el as HTMLInputElement)
                  }
                />
              </Grid>
            ))}
          </Grid>

          <ResendOtpTimer
            minutes={3}
            resendOtpType="auth"
            resetPreviousOtp={handleOtpReset}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <PrimaryButton
              type="submit"
              fullWidth
              disabled={!isComplete || isLoading}
            >
              <ButtonLoader title="Continue" isLoading={isLoading} />
            </PrimaryButton>
          </Box>
        </Box>
      </Box>

      <Toast
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default DocumentCollectionOtp;
